// @ts-check

import { useState, useImperativeHandle, useLayoutEffect, useRef } from 'react'
import { createContext, useContext } from 'react'
import { Loader2, ChevronDown } from 'lucide-react'
import { cn } from '@/lib/utils'
import { motion, AnimatePresence, useInView, LayoutGroup } from 'framer-motion'
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from './collapsible'

/**
 * Context for sharing step status, header hover state, and collapse state with child components
 */
const DeepResearchStepContext = createContext({
  /** @type {'pending' | 'completed'} */
  status: 'pending',
  /** @type {boolean} */
  isHeaderHovered: false,
  /** @type {(hovered: boolean) => void} */
  setIsHeaderHovered: () => {},
  /** @type {boolean} */
  isOpen: true
})

/**
 * @param {React.RefObject<HTMLElement>} bottomRef 
 * @param {React.RefObject<HTMLElement>} [containerRef]
 * @param {Object} [options]
 * @param {boolean} [options.enable] - Whether to enable scrolling to bottom
 */
function useScrollToBottom(bottomRef, containerRef, options = {}) {
  const isBottomVisible = useInView(bottomRef, {
    root: containerRef,
  })

  useLayoutEffect(() => {
    if(!options.enable) return
    return () => {
      const container = containerRef.current
      if(container == null || !(container instanceof HTMLDivElement)) return
      container.scrollTop = container.scrollHeight
    }
  }, [isBottomVisible, options.enable])
}

/**
 * DeepResearch - Main container for deep research steps
 * @param {Object} props
 * @param {string} [props.className] - Additional CSS classes
 * @param {React.ReactNode} props.children - DeepResearchStep components
 * @param {boolean} [props.shouldScrollToBottom]
 * @param {React.RefObject<HTMLDivElement>} [ref] - Ref for the container element
 */
function DeepResearch({ className, children, shouldScrollToBottom = true, ...props }, ref) {
  const containerRef = useRef(null)
  useImperativeHandle(ref, () => containerRef.current)
  const bottomRef = useRef(null)

  useScrollToBottom(bottomRef, containerRef, {
    enable: shouldScrollToBottom
  })

  return (
    <motion.div
      layout
      className={cn(
        'flex flex-col w-full bg-muted/50 border border-border rounded-xl p-0 overflow-x-hidden isolate',
        className
      )}
      ref={containerRef}
      {...props}
    >
      <LayoutGroup>
        <AnimatePresence mode="popLayout">
          {children}
        </AnimatePresence>
        <motion.div layout ref={bottomRef} />
      </LayoutGroup>
    </motion.div>
  )
}

/**
 * DeepResearchStep - Individual step card
 * @param {Object} props
 * @param {'pending' | 'completed'} [props.status] - Step status
 * @param {string} [props.className] - Additional CSS classes
 * @param {React.ReactNode} props.children - Step content (Header, Content, etc.)
 */
function DeepResearchStep({ status = 'pending', className, children, ...props }) {
  const [isHeaderHovered, setIsHeaderHovered] = useState(false)
  const [isOpen, setIsOpen] = useState(true)

  return (
    <DeepResearchStepContext.Provider value={{ status, isHeaderHovered, setIsHeaderHovered, isOpen }}>
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <motion.div
          layout
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ duration: 0.3, ease: 'easeOut' }}
          className={cn('flex flex-col border-t border-border first:border-t-0', className)}
          {...props}
        >
          {children}
        </motion.div>
      </Collapsible>
    </DeepResearchStepContext.Provider>
  )
}

/**
 * DeepResearchStepHeader - Header section for a step
 * @param {Object} props
 * @param {string} [props.className] - Additional CSS classes
 * @param {React.ReactNode} props.children - Icon and title components
 */
function DeepResearchStepHeader({ className, children, ...props }) {
  const { setIsHeaderHovered } = useContext(DeepResearchStepContext)

  return (
    <CollapsibleTrigger asChild>
      <motion.div
        layout
        className={cn(
          'flex items-center gap-2 z-10 py-4 px-4 cursor-pointer',
          className
        )}
        onMouseEnter={() => setIsHeaderHovered(true)}
        onMouseLeave={() => setIsHeaderHovered(false)}
        {...props}
      >
        {children}
      </motion.div>
    </CollapsibleTrigger>
  )
}

/**
 * DeepResearchStepIcon - Icon display with automatic spinner on loading
 * @param {Object} props
 * @param {string} [props.className] - Additional CSS classes
 * @param {React.ReactNode} props.children - Custom icon element
 */
function DeepResearchStepIcon({ className, children, ...props }) {
  const { status, isHeaderHovered, isOpen } = useContext(DeepResearchStepContext)

  return (
    <div className={cn('size-4 text-muted-foreground flex-shrink-0 relative', className)} {...props}>
      <AnimatePresence mode="wait">
        {status === 'pending' && (
          <motion.div
            key="spinner"
            className='size-4 [&>*]:size-4'
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            transition={{ duration: 0.05 }}
          >
            <Loader2 className="animate-spin" />
          </motion.div>
        )}
        {status === 'completed' && isHeaderHovered && (
          <motion.div
            key="chevron"
            className='size-4 [&>*]:size-4'
            initial={{ opacity: 0, scale: 0.8, rotate: isOpen ? 180 : 0 }}
            animate={{ opacity: 1, scale: 1, rotate: isOpen ? 180 : 0 }}
            exit={{ opacity: 0, scale: 0.8 }}
            transition={{ duration: 0.05 }}
          >
            <ChevronDown />
          </motion.div>
        )}
        {status === 'completed' && !isHeaderHovered && (
          <motion.div
            key="icon"
            className='size-4 [&>*]:size-4'
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            transition={{ duration: 0.05 }}
          >
            {children}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

/**
 * DeepResearchStepTitle - Title text for a step
 * @param {Object} props
 * @param {string} [props.className] - Additional CSS classes
 * @param {React.ReactNode} props.children - Title text
 */
function DeepResearchStepTitle({ className, children, ...props }) {
  return (
    <h3 className={cn('text-sm font-bold text-foreground truncate', className)} {...props}>
      {children}
    </h3>
  )
}

/**
 * DeepResearchStepContent - Content area for a step
 * @param {Object} props
 * @param {string} [props.className] - Additional CSS classes
 * @param {React.ReactNode} props.children - Step content
 */
function DeepResearchStepContent({ className, children, ...props }) {
  return (
    <CollapsibleContent>
      <div
        className={cn('sm:pl-10 pl-4 pr-4 pb-2 space-y-2 text-sm text-muted-foreground relative', className)}
        {...props}
      >
        {children}
      </div>
    </CollapsibleContent>
  )
}

// Compound component pattern for convenience
DeepResearch.Step = DeepResearchStep
DeepResearch.StepHeader = DeepResearchStepHeader
DeepResearch.StepIcon = DeepResearchStepIcon
DeepResearch.StepTitle = DeepResearchStepTitle
DeepResearch.StepContent = DeepResearchStepContent

export {
  DeepResearch,
  DeepResearchStep,
  DeepResearchStepHeader,
  DeepResearchStepIcon,
  DeepResearchStepTitle,
  DeepResearchStepContent
}
